import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import AdminService from '../../services/adminService';

/**
 * Loan Options Manager Component
 *
 * Manages loan options for immigration pathways:
 * - Configure loan providers and terms
 * - Set down payment percentages and interest rates
 * - Define repayment periods and requirements
 * - Multiple loan provider options
 */
const LoanOptionsManager = ({ pathway, onUpdate, onCancel }) => {
  const { t } = useLanguage();
  const [loanOptions, setLoanOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddLoan, setShowAddLoan] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);

  useEffect(() => {
    if (pathway?.id) {
      loadLoanOptions();
    }
  }, [pathway]);

  const loadLoanOptions = async () => {
    try {
      setLoading(true);
      const data = await AdminService.getLoanOptions(pathway.id);
      setLoanOptions(data || []);
    } catch (error) {
      console.error('Error loading loan options:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLoan = () => {
    setEditingLoan({
      loan_provider: '',
      loan_type: 'student_loan',
      down_payment_percentage: pathway.loan_down_payment_percentage || 25,
      interest_rate: 0,
      repayment_period_years: pathway.loan_repayment_period_years || 5,
      max_loan_amount: 0,
      min_credit_score: 650,
      requirements: '',
      is_active: true
    });
    setShowAddLoan(true);
  };

  const handleEditLoan = (loan) => {
    setEditingLoan(loan);
    setShowAddLoan(true);
  };

  const handleSaveLoan = async (loanData) => {
    try {
      setLoading(true);
      if (editingLoan.id) {
        await AdminService.updateLoanOption(editingLoan.id, loanData);
      } else {
        await AdminService.createLoanOption({
          ...loanData,
          pathway_id: pathway.id
        });
      }

      setShowAddLoan(false);
      setEditingLoan(null);
      await loadLoanOptions();
      onUpdate && onUpdate();
    } catch (error) {
      console.error('Error saving loan option:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLoan = async (loanId) => {
    if (!window.confirm('Are you sure you want to delete this loan option?')) {
      return;
    }

    try {
      await AdminService.deleteLoanOption(loanId);
      await loadLoanOptions();
      onUpdate && onUpdate();
    } catch (error) {
      console.error('Error deleting loan option:', error);
    }
  };

  if (loading && loanOptions.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-white">
            Loan Options
          </h3>
          <p className="text-sm text-gray-400">
            Configure loan providers and financing options for students
          </p>
        </div>
        <button
          onClick={handleAddLoan}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          Add Loan Option
        </button>
      </div>

      {/* Enable Loans Toggle */}
      <div className="bg-green-900/20 border border-green-800 rounded-lg p-4">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={pathway?.loan_enabled || false}
            onChange={async (e) => {
              if (onUpdate) {
                await onUpdate({ loan_enabled: e.target.checked });
              }
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
          />
          <span className="ml-2 text-sm font-medium text-gray-300">
            Enable loan options for this pathway
          </span>
        </label>
        <p className="mt-1 text-xs text-gray-400">
          When enabled, students can apply for loans with configurable terms and conditions
        </p>
      </div>

      {/* Default Loan Settings */}
      {pathway?.loan_enabled && (
        <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
          <h4 className="text-sm font-medium text-white mb-3">Default Loan Settings</h4>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">
                Default Down Payment (%)
              </label>
              <input
                type="number"
                value={pathway.loan_down_payment_percentage || 25}
                onChange={async (e) => {
                  if (onUpdate) {
                    await onUpdate({ loan_down_payment_percentage: parseFloat(e.target.value) || 25 });
                  }
                }}
                className="w-full px-2 py-1 text-sm border border-gray-600 rounded bg-gray-700 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Default Interest Rate (%)
              </label>
              <input
                type="number"
                value={pathway.loan_interest_rate || 0}
                onChange={async (e) => {
                  if (onUpdate) {
                    await onUpdate({ loan_interest_rate: parseFloat(e.target.value) || 0 });
                  }
                }}
                className="w-full px-2 py-1 text-sm border border-gray-600 rounded bg-gray-700 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                min="0"
                max="30"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Default Repayment Period (Years)
              </label>
              <input
                type="number"
                value={pathway.loan_repayment_period_years || 5}
                onChange={async (e) => {
                  if (onUpdate) {
                    await onUpdate({ loan_repayment_period_years: parseInt(e.target.value) || 5 });
                  }
                }}
                className="w-full px-2 py-1 text-sm border border-gray-600 rounded bg-gray-700 text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                min="1"
                max="30"
              />
            </div>
          </div>
        </div>
      )}

      {/* Loan Options List */}
      {loanOptions.length === 0 ? (
        <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-600 rounded-lg">
          No loan options configured yet. Click "Add Loan Option" to get started.
        </div>
      ) : (
        <div className="space-y-4">
          {loanOptions.map((loan) => (
            <div key={loan.id} className="p-4 border border-gray-700 rounded-lg bg-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <span className="px-2 py-1 bg-green-900/30 text-green-400 border border-green-800 text-xs font-medium rounded">
                      {loan.loan_type?.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className="font-medium text-white">
                      {loan.loan_provider}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-4 gap-4 text-sm text-gray-400">
                    <div>
                      Down Payment: <span className="font-semibold">{loan.down_payment_percentage}%</span>
                    </div>
                    <div>
                      Interest Rate: <span className="font-semibold">{loan.interest_rate}%</span>
                    </div>
                    <div>
                      Period: <span className="font-semibold">{loan.repayment_period_years} years</span>
                    </div>
                    <div>
                      Max Amount: <span className="font-semibold">${loan.max_loan_amount?.toLocaleString()}</span>
                    </div>
                  </div>
                  {loan.requirements && (
                    <p className="mt-2 text-sm text-gray-400">
                      Requirements: {loan.requirements}
                    </p>
                  )}
                  {!loan.is_active && (
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-300">
                        Inactive
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditLoan(loan)}
                    className="p-2 text-blue-400 hover:bg-blue-900/30 rounded-lg"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteLoan(loan.id)}
                    className="p-2 text-red-400 hover:bg-red-900/30 rounded-lg"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Loan Option Modal */}
      {showAddLoan && (
        <LoanForm
          loan={editingLoan}
          pathway={pathway}
          onSave={handleSaveLoan}
          onCancel={() => {
            setShowAddLoan(false);
            setEditingLoan(null);
          }}
        />
      )}
    </div>
  );
};

/**
 * Loan Form Component
 */
const LoanForm = ({ loan, pathway, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    loan_provider: loan?.loan_provider || '',
    loan_type: loan?.loan_type || 'student_loan',
    down_payment_percentage: loan?.down_payment_percentage || pathway?.loan_down_payment_percentage || 25,
    interest_rate: loan?.interest_rate || pathway?.loan_interest_rate || 0,
    repayment_period_years: loan?.repayment_period_years || pathway?.loan_repayment_period_years || 5,
    max_loan_amount: loan?.max_loan_amount || 0,
    min_credit_score: loan?.min_credit_score || 650,
    requirements: loan?.requirements || '',
    is_active: loan?.is_active !== undefined ? loan.is_active : true
  });

  const loanTypes = [
    { value: 'student_loan', label: 'Student Loan' },
    { value: 'education_loan', label: 'Education Loan' },
    { value: 'personal_loan', label: 'Personal Loan' },
    { value: 'alternative_loan', label: 'Alternative Loan' },
    { value: 'government_loan', label: 'Government Loan' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-white">
              {loan?.id ? 'Edit Loan Option' : 'Add Loan Option'}
            </h3>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Loan Provider
                </label>
                <input
                  type="text"
                  value={formData.loan_provider}
                  onChange={(e) => setFormData(prev => ({ ...prev, loan_provider: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
                  placeholder="e.g., National Bank, Immigration Finance"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Loan Type
                </label>
                <select
                  value={formData.loan_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, loan_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {loanTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Down Payment (%)
                  </label>
                  <input
                    type="number"
                    value={formData.down_payment_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, down_payment_percentage: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    max="100"
                    step="0.1"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Interest Rate (%)
                  </label>
                  <input
                    type="number"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, interest_rate: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    max="30"
                    step="0.1"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Repayment Period (Years)
                  </label>
                  <input
                    type="number"
                    value={formData.repayment_period_years}
                    onChange={(e) => setFormData(prev => ({ ...prev, repayment_period_years: parseInt(e.target.value) || 1 }))}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="30"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Max Loan Amount
                  </label>
                  <input
                    type="number"
                    value={formData.max_loan_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_loan_amount: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="1000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Minimum Credit Score
                </label>
                <input
                  type="number"
                  value={formData.min_credit_score}
                  onChange={(e) => setFormData(prev => ({ ...prev, min_credit_score: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="300"
                  max="850"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Requirements & Conditions
                </label>
                <textarea
                  value={formData.requirements}
                  onChange={(e) => setFormData(prev => ({ ...prev, requirements: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
                  placeholder="e.g., Proof of admission required, co-signer needed for international students"
                />
              </div>

              <div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-300">
                    Active Loan Option
                  </span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-700">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Loan Option
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoanOptionsManager;